import { useState } from 'react'
import AppLayout from '../../components/layout/AppLayout'
import { useBilling, type SubscriptionStatus } from '../../hooks/useBilling'
import { useToast } from '../../hooks/useToast'

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: 'Em teste (trial)',
  active: 'Ativa',
  past_due: 'Pagamento pendente',
  canceled: 'Cancelada',
  suspended: 'Suspensa',
}

function formatPrice(cents: number) {
  if (cents === 0) return 'Grátis'
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export default function PlansPage() {
  const { plans, subscription, loading, startCheckout } = useBilling()
  const { notify } = useToast()
  const [busyId, setBusyId] = useState<string | null>(null)

  async function handleSubscribe(planId: string) {
    setBusyId(planId)
    const r = await startCheckout(planId)
    if (!r.ok) notify('error', r.error ?? 'Falha ao iniciar checkout')
    setBusyId(null)
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-ink mb-1">Planos e assinatura</h1>
        {subscription && (
          <p className="text-sm text-ink-muted mb-6">
            Status atual: <span className="text-brand-600 dark:text-brand-300 font-semibold">{STATUS_LABEL[subscription.status]}</span>
            {subscription.status === 'trialing' && subscription.trial_ends_at &&
              ` · trial até ${new Date(subscription.trial_ends_at).toLocaleDateString('pt-BR')}`}
          </p>
        )}

        {loading ? (
          <p className="text-ink-faint text-sm">Carregando planos...</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map(plan => {
              const isCurrent = subscription?.plan_id === plan.id
              return (
                <div
                  key={plan.id}
                  className={`rounded-xl border p-5 flex flex-col ${
                    isCurrent ? 'border-brand-500 bg-brand-500/5' : 'border-line bg-surface'
                  }`}
                >
                  <h2 className="text-lg font-semibold text-ink">{plan.name}</h2>
                  <p className="text-2xl font-bold text-ink mt-2">
                    {formatPrice(plan.price_cents)}
                    {plan.price_cents > 0 && (
                      <span className="text-sm font-normal text-ink-faint">
                        /{plan.billing_period === 'monthly' ? 'mês' : 'ano'}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-ink-muted mt-2">Até {plan.max_users} usuários</p>
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={busyId === plan.id || isCurrent}
                    className="mt-auto pt-4 text-sm font-medium text-brand-400 hover:text-brand-300 disabled:opacity-40 disabled:cursor-not-allowed text-left"
                  >
                    {isCurrent ? 'Plano atual' : busyId === plan.id ? 'Abrindo checkout...' : 'Assinar'}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
